/**
 * Storage Service - Manages session and local storage for the widget.
 *
 * Primary namespace: `lumiari_chat_<scope>_<key>` (renamed from the
 * pre-v3 namespace in v3.0.0). A one-time, silent, never-throwing
 * migration runs at construction: for each known key, if a pre-v3
 * entry exists and the new key does not, the value is copied over;
 * the pre-v3 entry is then deleted (idempotent on next refresh).
 *
 * v2 namespace (canonical going forward): `lumiari:chat:<widget_key>:<key>`.
 * v2-only keys (shopper_token, widget_session_jwt, last_message_at)
 * live there exclusively; identifier keys are dual-written.
 *
 * Persistence policy (anti-leak):
 *   - localStorage: identifiers only — conversation_id, cart_id,
 *     session_id, guest user shape (name/email/phone), shopper_token
 *     (TTL <= 60min, auto-expired on read), last_message_at.
 *   - sessionStorage: cart preview state, message list (last 50, no
 *     content of streaming bubbles persisted), widget open/closed.
 *   - NEVER persist full message history with PII content beyond the
 *     window-scoped sessionStorage entry.
 *
 * conversation_id and cart_id are persisted in localStorage so a page
 * refresh resumes the same conversation server-side.
 */

const STORAGE_PREFIX = 'lumiari_chat_';
// Pre-v3 storage namespace. Referenced ONLY by the one-time migration in
// `migrateLegacyKeys()` — never written to. Drop once the migration
// window closes.
const LEGACY_STORAGE_PREFIX = 'tg_chat_';
const V2_PREFIX = 'lumiari:chat:';
const SHOPPER_TOKEN_TTL_MS = 60 * 60 * 1000; // 60 minutes — never persist beyond this.

/**
 * Resolve a Web Storage backend, falling back to an in-memory one.
 *
 * Merely READING `window.localStorage` throws a SecurityError in an iframe
 * sandboxed without `allow-same-origin` (opaque origin) — the Vesna
 * playground frame (`transport: 'host'`) — and when the browser blocks site
 * data. The widget must still mount there: it then keeps state for the life
 * of the page only, which is exactly what a sandboxed preview needs.
 */
function resolveStorage(name: 'localStorage' | 'sessionStorage'): Storage {
  try {
    const storage = window[name];
    storage.getItem(STORAGE_PREFIX);
    return storage;
  } catch {
    // Only the three calls StorageService makes; kept minimal on purpose
    // (this ships in every storefront bundle).
    const m = new Map<string, string>();
    return {
      getItem: (k: string) => (m.has(k) ? m.get(k) : null),
      setItem: (k: string, v: string) => void m.set(k, String(v)),
      removeItem: (k: string) => void m.delete(k),
    } as Storage;
  }
}

export class StorageService {
  private storeId: string;
  private widgetKey: string | null;
  private scope: string;
  /** this.l, or an in-memory stand-in when it is unreachable. */
  private l: Storage;
  /** this.s, or an in-memory stand-in when it is unreachable. */
  private s: Storage;

  constructor(storeId: string, widgetKey: string | null = null) {
    this.storeId = storeId || widgetKey || 'lumiari_widget';
    this.widgetKey = widgetKey && widgetKey.length > 0 ? widgetKey : null;
    this.scope = this.widgetKey || this.storeId;
    this.l = resolveStorage('localStorage');
    this.s = resolveStorage('sessionStorage');

    // Best-effort one-shot migration of pre-v3 entries (old namespace
    // prefix, and pre-widgetKey storeId scope) into the current keys.
    this.migrateLegacyKeys();
  }

  private getKey(key: string): string {
    // v1-shaped namespace (underscore-delimited), retained as the
    // primary read/write surface during the v2 transition window so
    // existing widgets in flight don't lose state. v2 namespace is
    // dual-written via `getKeyV2()` below; once every consumer has
    // flipped to v2, this namespace can be deleted.
    return `${STORAGE_PREFIX}${this.scope}_${key}`;
  }

  private getKeyV2(key: string): string {
    return `${V2_PREFIX}${this.scope}:${key}`;
  }

  /**
   * v2-aware writer used by the new entry point. Mirrors the value to
   * the v2 namespace alongside the v1 write. Idempotent — safe to call
   * even when storage is unavailable (CSP / private mode).
   */
  private writeV2(storage: Storage | undefined, key: string, value: string): void {
    if (!storage) return;
    try {
      storage.setItem(this.getKeyV2(key), value);
    } catch {
      // Ignore — v1 write already succeeded.
    }
  }

  /**
   * One-time, silent migration of pre-v3 entries into the current
   * namespace. Two legacy slots are checked per key, most specific
   * first:
   *
   *   1. `tg_chat_<scope>_*`   — pre-v3 namespace, current scope.
   *   2. `tg_chat_<storeId>_*` — pre-v3 namespace, pre-widgetKey
   *      storeId scope (only when it differs from the current scope).
   *
   * If the new `lumiari_chat_<scope>_*` key already has a value it is
   * left untouched (never clobber a more recent value); legacy slots
   * are always cleared so the migration is idempotent on next refresh.
   * Never throws — storage may be unavailable (CSP / private mode).
   */
  private migrateLegacyKeys(): void {
    const localKeys = ['session_id', 'user', 'conversation_id', 'cart_id'];
    const sessionKeys = ['cart', 'messages', 'widget_state'];

    const legacyKeysFor = (key: string): string[] => {
      const keys = [`${LEGACY_STORAGE_PREFIX}${this.scope}_${key}`];
      if (this.storeId !== this.scope) {
        keys.push(`${LEGACY_STORAGE_PREFIX}${this.storeId}_${key}`);
      }
      return keys;
    };

    const migrate = (storage: Storage | undefined, key: string) => {
      if (!storage) return;
      try {
        const newKey = this.getKey(key);
        for (const legacyKey of legacyKeysFor(key)) {
          const legacyVal = storage.getItem(legacyKey);
          if (legacyVal !== null && storage.getItem(newKey) === null) {
            storage.setItem(newKey, legacyVal);
          }
          storage.removeItem(legacyKey);
        }
      } catch {
        // CSP / private mode — silently skip; nothing to migrate.
      }
    };

    localKeys.forEach((k) => migrate(this.l, k));
    sessionKeys.forEach((k) => migrate(this.s, k));
  }

  // Session ID
  getSessionId(): string | null {
    return this.l.getItem(this.getKey('session_id'));
  }

  setSessionId(sessionId: string): void {
    this.l.setItem(this.getKey('session_id'), sessionId);
    this.writeV2(this.l, 'session_id', sessionId);
  }

  clearSessionId(): void {
    this.l.removeItem(this.getKey('session_id'));
  }

  // Conversation ID (server-issued). Persisted in localStorage so refresh
  // resumes the same conversation_id when sending the next message.
  getConversationId(): string | null {
    return this.l.getItem(this.getKey('conversation_id'));
  }

  setConversationId(conversationId: string): void {
    this.l.setItem(this.getKey('conversation_id'), conversationId);
    this.writeV2(this.l, 'conversation_id', conversationId);
    this.touchLastMessage();
  }

  clearConversationId(): void {
    this.l.removeItem(this.getKey('conversation_id'));
  }

  // Cart ID (server-issued). Persisted in localStorage so a refresh keeps
  // the cart attached to the same shopper.
  getCartId(): string | null {
    return this.l.getItem(this.getKey('cart_id'));
  }

  setCartId(cartId: string): void {
    this.l.setItem(this.getKey('cart_id'), cartId);
    this.writeV2(this.l, 'cart_id', cartId);
  }

  /**
   * shopper_token (TTL <= 60min). Stored in localStorage with an
   * expiry stamp so a stale token is never returned.
   * NEVER persisted to v1 namespace — this is a v2-only surface.
   */
  getShopperToken(): string | null {
    try {
      const raw = this.l.getItem(this.getKeyV2('shopper_token'));
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { token?: string; expires_at?: number };
      if (!parsed.token || !parsed.expires_at) return null;
      if (Date.now() > parsed.expires_at) {
        this.l.removeItem(this.getKeyV2('shopper_token'));
        return null;
      }
      return parsed.token;
    } catch {
      return null;
    }
  }

  setShopperToken(token: string, ttlMs: number = SHOPPER_TOKEN_TTL_MS): void {
    if (!token) return;
    const ttl = Math.min(Math.max(ttlMs, 0), SHOPPER_TOKEN_TTL_MS);
    const payload = JSON.stringify({ token, expires_at: Date.now() + ttl });
    try {
      this.l.setItem(this.getKeyV2('shopper_token'), payload);
    } catch {
      // Storage unavailable — caller proceeds without token persistence.
    }
  }

  clearShopperToken(): void {
    try {
      this.l.removeItem(this.getKeyV2('shopper_token'));
    } catch {
      // ignore
    }
  }

  getWidgetSessionToken(): string | null {
    try {
      return this.l.getItem(this.getKeyV2('widget_session_jwt'));
    } catch {
      return null;
    }
  }

  setWidgetSessionToken(token: string): void {
    if (!token) return;
    try {
      this.l.setItem(this.getKeyV2('widget_session_jwt'), token);
    } catch {
      // Storage unavailable — bootstrap falls back to the widget key.
    }
  }

  clearWidgetSessionToken(): void {
    try {
      this.l.removeItem(this.getKeyV2('widget_session_jwt'));
    } catch {
      // ignore
    }
  }

  /** Last-message-at touch — used for idle expiry sweepers. */
  private touchLastMessage(): void {
    try {
      this.l.setItem(
        this.getKeyV2('last_message_at'),
        String(Date.now()),
      );
    } catch {
      // ignore
    }
  }

  getLastMessageAt(): number | null {
    try {
      const raw = this.l.getItem(this.getKeyV2('last_message_at'));
      if (!raw) return null;
      const ts = parseInt(raw, 10);
      return Number.isFinite(ts) ? ts : null;
    } catch {
      return null;
    }
  }

  clearCartId(): void {
    this.l.removeItem(this.getKey('cart_id'));
  }

  // User data
  getUser(): { name?: string; email?: string; phone?: string } | null {
    const data = this.l.getItem(this.getKey('user'));
    return data ? JSON.parse(data) : null;
  }

  setUser(user: { name?: string; email?: string; phone?: string }): void {
    this.l.setItem(this.getKey('user'), JSON.stringify(user));
  }

  clearUser(): void {
    this.l.removeItem(this.getKey('user'));
  }

  // Cart
  getCart(): { items: any[]; total: number } | null {
    const data = this.s.getItem(this.getKey('cart'));
    return data ? JSON.parse(data) : null;
  }

  setCart(cart: { items: any[]; total: number }): void {
    this.s.setItem(this.getKey('cart'), JSON.stringify(cart));
  }

  clearCart(): void {
    this.s.removeItem(this.getKey('cart'));
  }

  // Messages history (last N messages for context)
  getMessages(): any[] {
    const data = this.s.getItem(this.getKey('messages'));
    return data ? JSON.parse(data) : [];
  }

  setMessages(messages: any[]): void {
    // Keep only last 50 messages
    const toStore = messages.slice(-50);
    this.s.setItem(this.getKey('messages'), JSON.stringify(toStore));
  }

  clearMessages(): void {
    this.s.removeItem(this.getKey('messages'));
  }

  // Widget state (open/closed)
  getWidgetState(): { isOpen: boolean } | null {
    const data = this.s.getItem(this.getKey('widget_state'));
    return data ? JSON.parse(data) : null;
  }

  setWidgetState(state: { isOpen: boolean }): void {
    this.s.setItem(this.getKey('widget_state'), JSON.stringify(state));
  }

  // Clear all data for this store
  clearAll(): void {
    this.clearSessionId();
    this.clearConversationId();
    this.clearCartId();
    this.clearUser();
    this.clearCart();
    this.clearMessages();
    this.clearShopperToken();
    this.clearWidgetSessionToken();
    try {
      // v2 namespace mirrors — explicit drop keeps storage clean for
      // shared devices.
      const keysToDrop = [
        'session_id',
        'conversation_id',
        'cart_id',
        'user',
        'last_message_at',
        'widget_session_jwt',
      ];
      keysToDrop.forEach((k) => {
        try {
          this.l.removeItem(this.getKeyV2(k));
        } catch {
          // ignore
        }
      });
    } catch {
      // ignore
    }
  }
}
